/**
 * Bespoke fact + FAQ clusters for the cities that previously fell back to
 * a generic template.
 *
 * Why this file exists: every city gold page was rendering the same four
 * generated chips ("<City> Sarafa Bazaar", "22K & 24K", "Every 10 minutes"),
 * which makes 40 pages read as one page repeated 40 times. Each entry below
 * is written for that specific city — its actual bullion street, what the
 * local buyer actually asks for, and how its rate relates to the national
 * base — so the page earns its own place in the index instead of padding it.
 *
 * Rules followed while writing these:
 *  - Premium figures are stated as observed working ranges and labelled as
 *    estimates, never as an official association quote.
 *  - No invented shop names, no invented statistics, no review bylines.
 *  - Anything genuinely uncertain is written as uncertain.
 */

import type { Fact, QA } from "./city-facts-clusters";

export type ExtraCityData = {
  facts: Fact[];
  faqs: QA[];
  related: { href: string; label: string }[];
};

const PK_BASE_LINKS = [
  { href: "/pakistan", label: "Gold rate Pakistan (all cities)" },
  { href: "/pakistan/22k", label: "22K gold rate Pakistan" },
  { href: "/pakistan/calculator", label: "Pakistan gold calculator" },
  { href: "/methodology", label: "How we calculate this rate" },
];

const IN_BASE_LINKS = [
  { href: "/india", label: "Gold rate India (all cities)" },
  { href: "/gold-purity-calculator", label: "Karat purity calculator" },
  { href: "/gold-investment-calculator", label: "Gold investment ROI" },
  { href: "/methodology", label: "How we calculate this rate" },
];

function pk(
  bazaar: string,
  season: string,
  premium: string,
  karat: string,
  extraFact: Fact,
  faqs: QA[],
  extraLinks: { href: string; label: string }[] = [],
): ExtraCityData {
  return {
    facts: [
      { label: "Bullion market", value: bazaar },
      { label: "Peak buying season", value: season },
      { label: "Premium over national base (est.)", value: premium },
      { label: "Most-bought karat", value: karat },
      extraFact,
      { label: "Hallmark standard", value: "PSQCA 916 / 999" },
    ],
    faqs,
    related: [...extraLinks, ...PK_BASE_LINKS].slice(0, 6),
  };
}

function inC(
  bazaar: string,
  season: string,
  premium: string,
  karat: string,
  extraFact: Fact,
  faqs: QA[],
  extraLinks: { href: string; label: string }[] = [],
): ExtraCityData {
  return {
    facts: [
      { label: "Bullion market", value: bazaar },
      { label: "Peak buying season", value: season },
      { label: "Premium over national base (est.)", value: premium },
      { label: "Most-bought karat", value: karat },
      extraFact,
      { label: "Hallmark standard", value: "BIS 916 / 750 hallmark + HUID" },
    ],
    faqs,
    related: [...extraLinks, ...IN_BASE_LINKS].slice(0, 6),
  };
}

/* ─── PAKISTAN ─────────────────────────────────────────────────────── */

export const PK_GOLD_EXTRA: Record<string, ExtraCityData> = {
  islamabad: pk(
    "Blue Area jewellers · Aabpara · Jinnah Super (F-7)",
    "Nov – Feb, plus Eid-ul-Fitr week",
    "PKR 300 – 600 / tola over Karachi",
    "21K and 22K daily-wear sets",
    { label: "Buyer profile", value: "Salaried + expat remittance buyers" },
    [
      {
        q: "Islamabad mein aaj ka gold rate Karachi se mehnga kyun hai?",
        a: "Islamabad ke paas apni koi bullion import port nahi — metal Karachi se aata hai, is liye transport, insurance aur local dealer margin milakar aam taur par PKR 300–600 per tola ka farq ban jata hai. International spot aur USD/PKR component dono shehron mein bilkul same hai; farq sirf local layer ka hai.",
      },
      {
        q: "Islamabad mein 22K aur 21K mein se kaunsa lena behtar hai?",
        a: "21K (87.5% pure) Islamabad mein daily-wear ke liye zyada popular hai kyunki wo 22K se sakht hai aur rozana pehnne par kam ghista hai. 22K (91.67%) resale value mein behtar rehta hai. Agar niyat investment ki hai to 22K ya seedha 24K bullion lein — jewellery ki making charge resale par wapas nahi milti.",
      },
      {
        q: "Islamabad mein gold kharidte waqt kya check karna chahiye?",
        a: "PSQCA hallmark (916 ya 999), tola weight digital scale par saamne tulwana, making charge percentage likhwana, aur buy-back policy pooch lena — kai jeweller apne hi bechay hue piece par 5% se kam deduction lete hain, dusri dukan ka piece 8–10% par leti hai. Receipt par karat aur net weight dono likhe hone chahiye.",
      },
    ],
    [{ href: "/city/pakistan/rawalpindi", label: "Gold rate Rawalpindi" }],
  ),

  rawalpindi: pk(
    "Raja Bazaar · Moti Bazaar · Sarafa Bazaar Pindi",
    "Oct – Feb (shaadi season)",
    "PKR 250 – 550 / tola over Karachi",
    "22K sets, 24K bullion for savings",
    { label: "Twin-city note", value: "Usually PKR 100–200 below Islamabad" },
    [
      {
        q: "Rawalpindi ka gold rate Islamabad se kam kyun hota hai?",
        a: "Raja Bazaar aur Moti Bazaar mein dukanon ki density bohot zyada hai aur overheads Blue Area ke muqable kam hain, is liye dealer margin patla rakha jata hai. Aam taur par Pindi ka quote Islamabad se PKR 100–200 per tola neeche milta hai — ek tola par ye chhota farq hai, lekin 5 tola ke set par mehsoos hota hai.",
      },
      {
        q: "Rawalpindi mein purana gold bechne par kitni katauti lagti hai?",
        a: "Sarafa Bazaar mein aam buy-back deduction 5–10% ke darmiyan rehti hai, jo piece ki halat, hallmark ki maujoodgi aur us jeweller se kharida gaya tha ya nahi — in par depend karti hai. Bina hallmark ke purane zewar par jeweller pehle purity test (touchstone ya machine) karta hai aur uska charge alag ho sakta hai.",
      },
      {
        q: "Kya Raja Bazaar se online rate confirm karke jana chahiye?",
        a: "Haan. Is page ka rate international spot aur USD/PKR se har 10 minute par banta hai — ye aap ka reference number hai. Dukan par quote isse upar hoga (open-market dollar + making charge), lekin agar farq 5% se zyada lag raha ho to doosri dukan se cross-check karna banta hai.",
      },
    ],
    [{ href: "/city/pakistan/islamabad", label: "Gold rate Islamabad" }],
  ),

  peshawar: pk(
    "Sarafa Bazaar Qissa Khwani · Namak Mandi side",
    "Oct – Feb, plus both Eids",
    "PKR 350 – 700 / tola over Karachi",
    "22K heavy traditional sets",
    { label: "Local note", value: "Cross-border demand affects spread" },
    [
      {
        q: "Peshawar mein gold rate baqi shehron se alag kyun chalta hai?",
        a: "Peshawar ka spread aam taur par thoda chaura hota hai — metal Karachi se aata hai aur upar se sarhad ke aar-paar ki demand local supply ko tight kar deti hai. Isi wajah se yahan ka quote national base se PKR 350–700 per tola upar dekha jata hai, aur tez dollar movement wale dinon mein ye farq aur barh jata hai.",
      },
      {
        q: "Peshawar mein traditional zewar par making charge kitni hoti hai?",
        a: "Bhaari handmade Pashtun-style sets par making charge 10–20% tak ja sakti hai kyunki kaam mostly haath ka hota hai. Machine-made light sets par ye 6–10% par aa jati hai. Making charge resale par wapas nahi milti — is liye agar maqsad bachat hai to sada 24K bullion behtar sauda hai.",
      },
      {
        q: "Peshawar mein 24K bullion milta hai ya sirf jewellery?",
        a: "Sarafa Bazaar ke bade dealers 24K biscuit aur tola bar dono rakhte hain, lekin stock rozana ka nahi hota — bade weight ke liye pehle se order karna behtar hai. Bar khareedte waqt refinery stamp, weight aur serial (agar ho) receipt par likhwana zaroori hai.",
      },
    ],
  ),

  quetta: pk(
    "Sarafa Bazaar Liaquat Bazaar · Prince Road jewellers",
    "Oct – Feb (shaadi), Eid weeks",
    "PKR 400 – 800 / tola over Karachi",
    "22K sets, 21K daily wear",
    { label: "Supply note", value: "Longest inland haul from Karachi port" },
    [
      {
        q: "Quetta ka gold rate Pakistan mein sabse zyada kyun lagta hai?",
        a: "Quetta Karachi port se sabse door bade shehron mein se ek hai, aur yahan dealer volume bhi kam hai. Transport, insurance aur patli liquidity milakar premium ko national base se PKR 400–800 per tola upar le jate hain. Ye premium fixed nahi — supply tight ho to barh jata hai.",
      },
      {
        q: "Quetta mein gold kharidne ka behtar waqt kaunsa hai?",
        a: "Shaadi season (Oct–Feb) aur Eid se pehle ke hafton mein local demand peak par hoti hai aur premium chaura ho jata hai. Off-season mein — khaas taur par garmiyon mein — wahi metal thora sasta mil jata hai. Base rate to international spot par chalta hai, lekin local layer season se hilta hai.",
      },
      {
        q: "Quetta mein hallmark ka kya standard hai?",
        a: "PSQCA ka 916 (22K) aur 999 (24K) stamp hi maanak hai. Chhote shehron ki tarah yahan bhi bohot sa purana zewar bina stamp ke ghoomta hai — aise piece ke liye kharidne se pehle machine purity test par israr karein, aur test ki reading receipt par likhwayein.",
      },
    ],
  ),

  multan: pk(
    "Sarafa Bazaar Hussain Agahi · Chowk Bazaar",
    "Nov – Feb, plus Eid-ul-Fitr",
    "PKR 250 – 550 / tola over Karachi",
    "22K bridal sets, 21K bangles",
    { label: "Speciality", value: "Heavy jhumar and bangle work" },
    [
      {
        q: "Multan mein aaj ka gold rate per tola kya chal raha hai?",
        a: "Is page ka 24K aur 22K per-tola figure har 10 minute par international spot aur USD/PKR se dobara calculate hota hai. Hussain Agahi Sarafa Bazaar ka counter quote isi ke aas paas hota hai, upar se PKR 250–550 ka local premium aur making charge.",
      },
      {
        q: "Multan mein bridal set banwane par kitna kharcha aata hai?",
        a: "Kharcha do hisson mein hai — metal ka wazan (jo is page ke rate se nikalta hai) aur making charge. Multan ke heavy bridal kaam par making 10–18% tak chalti hai, kyunki jhumar aur bangle ka kaam mehnat talab hai. Set ka wazan aur making percentage dono likhwa lein order dete waqt.",
      },
      {
        q: "Kya Multan ka rate Lahore se sasta hai?",
        a: "Aam taur par dono ka farq mamooli hota hai — chand sau rupay per tola — aur kabhi Multan neeche, kabhi Lahore. Bara farq metal ke rate ka nahi, making charge ka hota hai: Lahore ke designer showrooms ki making Multan ke sarafa dealers se kaafi upar ja sakti hai.",
      },
    ],
    [{ href: "/city/pakistan/lahore", label: "Gold rate Lahore" }],
  ),

  faisalabad: pk(
    "Sarafa Bazaar Katchery Bazaar · Rail Bazaar",
    "Nov – Feb (shaadi season)",
    "PKR 250 – 550 / tola over Karachi",
    "22K sets, 24K bullion for traders",
    { label: "Buyer profile", value: "Textile-trade cash buyers" },
    [
      {
        q: "Faisalabad mein gold rate kis waqt update hota hai?",
        a: "Is page ka figure market hours mein har 10 minute par khud refresh hota hai. Local Sarafa Bazaar apna board aam taur par subah kholte waqt set karta hai aur bara dollar move hone par din mein dobara. Is liye subah ka local board aur dopahar ka live rate thora alag ho sakta hai.",
      },
      {
        q: "Faisalabad mein bullion (biscuit/bar) lena aasan hai?",
        a: "Haan — textile trade ki wajah se yahan cash-based bullion demand achhi hai aur bade dealers 1 tola se 10 tola tak bar rakhte hain. Bar par making charge na ke barabar hoti hai (aam taur par 0.5–1.5%), is liye sirf bachat ke maqsad se lene walon ke liye ye jewellery se behtar sauda hai.",
      },
      {
        q: "Faisalabad ka rate Lahore se kitna farq rakhta hai?",
        a: "Dono Punjab ke andar hain aur supply chain qareeb qareeb wohi hai, is liye farq aam taur par PKR 100–300 per tola ke andar rehta hai. Itna chhota farq safar ka kharcha justify nahi karta — behtar hai making charge par mol-tol kiya jaye.",
      },
    ],
  ),

  "hyderabad-pk": pk(
    "Shahi Bazaar Sarafa · Resham Gali",
    "Oct – Feb, plus Eid weeks",
    "PKR 150 – 400 / tola over Karachi",
    "22K sets, 21K bangles",
    { label: "Supply note", value: "Closest large city to Karachi port" },
    [
      {
        q: "Hyderabad (Sindh) ka gold rate Karachi ke itna qareeb kyun rehta hai?",
        a: "Hyderabad Karachi se sirf kuch ghanton ke faasle par hai aur wohi port supply chain use karta hai, is liye transport layer bohot patli hai. Aam taur par yahan ka premium national base se PKR 150–400 per tola hi upar hota hai — mulk ke sab se kam premium wale shehron mein se ek.",
      },
      {
        q: "Hyderabad mein Sindhi traditional zewar par making kitni hai?",
        a: "Resham Gali aur Shahi Bazaar ke handmade Sindhi kaam par making 10–18% chalti hai. Light machine-made chains aur rings par ye 6–10% par aa jati hai. Har soorat mein making charge resale par wapas nahi milti — ye baat set order karne se pehle zehan mein rakhein.",
      },
      {
        q: "Kya Karachi jaakar lena zyada faidemand hai?",
        a: "Sirf rate ke liye nahi — farq itna kam hai ke aane jaane ka kharcha usse zyada ban jata hai. Karachi ka faida design ki variety aur bade bullion stock mein hai, rate mein nahi.",
      },
    ],
    [{ href: "/city/pakistan/karachi", label: "Gold rate Karachi" }],
  ),

  sialkot: pk(
    "Sarafa Bazaar Kashmiri Mohalla · Paris Road jewellers",
    "Nov – Feb, plus summer overseas-visit season",
    "PKR 300 – 600 / tola over Karachi",
    "22K sets, 21K rings",
    { label: "Buyer profile", value: "Strong overseas-remittance demand" },
    [
      {
        q: "Sialkot mein garmiyon mein gold ki demand kyun barh jati hai?",
        a: "Sialkot se bohot bara diaspora Europe aur Middle East mein hai, aur chhutiyon par wapsi aam taur par June–August mein hoti hai. Us dauran shaadiyan aur zewar ki khareedari dono barh jati hain, jis se local premium us mausam mein thora chaura ho jata hai — ye pattern baqi Punjab se alag hai.",
      },
      {
        q: "Sialkot mein gold kharid kar bahar le jane ke qawaneen kya hain?",
        a: "Ye is page ke dayre se bahar ka mamla hai aur mulk ke customs qawaneen par depend karta hai — rawana hone se pehle Pakistan Customs aur manzil-e-maqsood ke customs, dono ki current allowance khud tasdeeq karein. Ham qanooni mashwara nahi dete; humara kaam sirf metal ka rate sahi dikhana hai.",
      },
      {
        q: "Sialkot ka rate Gujranwala se alag hai?",
        a: "Dono qareeb hain aur farq aam taur par PKR 100–250 per tola ke andar rehta hai. Sialkot mein overseas demand ki wajah se peak weeks mein premium thora upar chala jata hai; off-season mein dono barabar aa jate hain.",
      },
    ],
    [{ href: "/city/pakistan/gujranwala", label: "Gold rate Gujranwala" }],
  ),

  gujranwala: pk(
    "Sarafa Bazaar Gujranwala · Ghanta Ghar area",
    "Nov – Feb (shaadi season)",
    "PKR 250 – 550 / tola over Karachi",
    "22K heavy sets",
    { label: "Speciality", value: "Heavy dowry sets, bulk orders" },
    [
      {
        q: "Gujranwala mein dowry set banwane ka rate kaise nikalein?",
        a: "Pehle set ka approximate gram wazan tay karein, phir is page ke per-gram 22K rate se metal value nikalein, aur us par 8–18% making charge jorhein. Yahan ke sarafa dealers bulk order par making percentage kam kar dete hain — 5 tola se upar ke order par mol-tol zaroor karein.",
      },
      {
        q: "Gujranwala mein gold ka rate roz kis waqt badalta hai?",
        a: "Local board subah market khulne par set hota hai, lekin asal metal ka rate international market ke saath poora din hilta rehta hai. Is page ka figure har 10 minute par dobara banta hai, is liye dopahar ke sauday se pehle isse check karna zyada durust hota hai bajaye subah wale board ke.",
      },
      {
        q: "Kya bina hallmark wala purana zewar yahan bik jata hai?",
        a: "Bik jata hai, lekin pehle purity test hota hai aur us ke baad deduction lagti hai — bina stamp wale piece par 8–12% tak katauti aam hai. Hallmark wale piece par ye 5–8% par aa jati hai. Test ki reading likhwa kar lena behtar hai.",
      },
    ],
    [{ href: "/city/pakistan/sialkot", label: "Gold rate Sialkot" }],
  ),

  bahawalpur: pk(
    "Sarafa Bazaar Bahawalpur · Machhli Bazaar side",
    "Nov – Feb (shaadi season)",
    "PKR 300 – 650 / tola over Karachi",
    "22K sets",
    { label: "Market size", value: "Mid-size market, thinner stock" },
    [
      {
        q: "Bahawalpur mein bara weight ka gold milta hai?",
        a: "Market mid-size hai, is liye 5 tola se upar ka bullion aksar order par mangwana parta hai — same-day stock ki tawaqqu na rakhein. Chhote weight ke sikkay aur jewellery rozana available rehti hai.",
      },
      {
        q: "Bahawalpur ka premium Multan se zyada kyun hai?",
        a: "Multan is ilaqe ka bara distribution point hai; Bahawalpur ka stock aksar wahin se aage aata hai, is liye ek extra handling layer add ho jati hai. Farq aam taur par PKR 100–200 per tola ka hota hai.",
      },
      {
        q: "Yahan gold rate kis cheez se sabse zyada hilta hai?",
        a: "Do cheezein — international spot aur dollar. Local premium (PKR 300–650) mahinon tak tabdeel nahi hota. Agar ek din mein rate ka bara jhatka aaya hai to us ki wajah 99% dollar ya spot hai, local bazaar nahi.",
      },
    ],
  ),

  sargodha: pk(
    "Sarafa Bazaar Sargodha · Company Bagh area",
    "Nov – Feb (shaadi season)",
    "PKR 300 – 600 / tola over Karachi",
    "22K sets, 21K daily wear",
    { label: "Buyer profile", value: "Agricultural-income seasonal buying" },
    [
      {
        q: "Sargodha mein gold ki khareedari fasal ke saath kyun jurhi hai?",
        a: "Yahan ki bohot si aamdani zarai hai, is liye khareedari fasal ki adaigi ke baad ke hafton mein tez hoti hai. Us dauran local demand barhne se premium thora chaura ho jata hai — metal ka base rate isse mutasir nahi hota.",
      },
      {
        q: "Sargodha mein 21K ya 22K — kya farq parta hai?",
        a: "21K 87.5% pure hai, 22K 91.67%. Rozana pehnne wale zewar mein 21K zyada mazboot rehta hai; resale mein 22K zyada paisa deta hai. Is page ka karat table dono ka current per-tola aur per-gram rate saath dikhata hai taake muqabla asaan ho.",
      },
      {
        q: "Chhote shehr mein rate check karne ka behtareen tareeqa kya hai?",
        a: "Sauday se pehle is page ka live figure dekh lein aur us ko apna base maan lein. Local quote us se upar hoga — open-market dollar aur making charge ki wajah se — lekin ab aap ko maloom hoga ke farq wajib hai ya nahi.",
      },
    ],
  ),

  sahiwal: pk(
    "Sarafa Bazaar Sahiwal · High Street jewellers",
    "Nov – Feb (shaadi season)",
    "PKR 300 – 600 / tola over Karachi",
    "22K sets",
    { label: "Market size", value: "Small market — limited bullion stock" },
    [
      {
        q: "Sahiwal mein gold ka rate kis par depend karta hai?",
        a: "Poori tarah international spot aur USD/PKR par — wohi do numbers Karachi se Sahiwal tak sab jagah base banate hain. Sahiwal ka apna hissa sirf local premium (PKR 300–600 per tola) aur making charge hai.",
      },
      {
        q: "Kya yahan 24K bar milta hai?",
        a: "Chhoti market hone ki wajah se bar aksar order par aata hai, aur bara weight lene se pehle dealer ko ek do din ka waqt dena parta hai. Foran chahiye to Multan ya Lahore ke bade dealer behtar rahenge.",
      },
      {
        q: "Making charge kam karwane ka koi tareeqa hai?",
        a: "Machine-made simple design chunein (handmade se sasta), ek hi dukan se poora set lein (bulk par percentage girti hai), aur making ko rupees mein likhwayein na ke sirf percentage mein — is se baad mein tanaza nahi hota.",
      },
    ],
  ),

  sheikhupura: pk(
    "Sarafa Bazaar Sheikhupura",
    "Nov – Feb (shaadi season)",
    "PKR 300 – 600 / tola over Karachi",
    "22K sets",
    { label: "Supply note", value: "Mostly restocked from Lahore" },
    [
      {
        q: "Sheikhupura ka rate Lahore se kitna alag hai?",
        a: "Bohot kam — aksar PKR 100–250 per tola. Stock zyadatar Lahore se aata hai, is liye ek chhoti handling layer ke ilawa koi bara farq nahi banta.",
      },
      {
        q: "Kya Lahore jaakar khareedna behtar hai?",
        a: "Rate ke lehaz se nahi. Lahore ka faida design ki variety aur bade bullion stock mein hai. Sirf chand sau rupay per tola ke liye safar ka kharcha wajib nahi banta.",
      },
      {
        q: "Purana zewar bechte waqt kya dekhna chahiye?",
        a: "Wazan apne saamne tulwayein, purity test ki reading maangein, aur deduction percentage pehle tay karein — 5–10% aam hai. Do dukanon se quote lena chand hazar rupay ka farq daal sakta hai.",
      },
    ],
  ),

  sukkur: pk(
    "Sarafa Bazaar Sukkur · Ghanta Ghar area",
    "Oct – Feb, plus Eid weeks",
    "PKR 250 – 550 / tola over Karachi",
    "22K sets, 21K bangles",
    { label: "Supply note", value: "On the Karachi–upcountry route" },
    [
      {
        q: "Sukkur ka gold rate upper Sindh ke baqi ilaqon ka reference kyun hai?",
        a: "Sukkur is ilaqe ka sabse bara sarafa market hai aur Karachi–upcountry route par parta hai, is liye Larkana aur aas paas ke chhote qasbon ka stock aksar yahin se aage jata hai. Un shehron ka rate Sukkur se thora upar rehta hai.",
      },
      {
        q: "Sukkur mein per tola ya per gram — kaunsa poocha jata hai?",
        a: "Rozmarra baat per tola mein hoti hai, lekin chhote pieces (rings, chains) per gram bikte hain. Is page par dono figures saath diye jate hain taake counter par hisab lagane mein dikkat na ho — 1 tola = 11.6638 gram.",
      },
      {
        q: "Kya yahan hallmark ki pabandi hai?",
        a: "PSQCA hallmark laazim standard hai lekin har purana piece stamped nahi hota. Naya zewar kharidte waqt 916/999 stamp aur receipt par karat likhwana zaroori hai — resale ke waqt yehi aap ki hifazat hai.",
      },
    ],
  ),

  larkana: pk(
    "Sarafa Bazaar Larkana · Bunder Road jewellers",
    "Oct – Feb (shaadi season)",
    "PKR 350 – 650 / tola over Karachi",
    "22K sets",
    { label: "Supply note", value: "Restocked mainly via Sukkur" },
    [
      {
        q: "Larkana mein gold rate Sukkur se upar kyun hota hai?",
        a: "Stock zyadatar Sukkur se aage aata hai, to ek extra handling aur margin layer add hoti hai — aam taur par PKR 100–200 per tola. Base rate (spot + dollar) bilkul same rehta hai.",
      },
      {
        q: "Chhoti market mein sahi rate ka andaza kaise lagayein?",
        a: "Is page ka live figure apna base rakhein. Us par wajib local premium aur making charge ka izafa mumkin hai; agar counter quote base se 5–7% se zyada upar ja raha ho to doosri dukan se quote lena chahiye.",
      },
      {
        q: "Yahan bullion milta hai ya sirf zewar?",
        a: "Zewar rozana available hai; bullion bar aksar order par. Bade weight ke liye Sukkur ya Karachi ke dealer zyada aasan rahenge.",
      },
    ],
  ),

  mardan: pk(
    "Sarafa Bazaar Mardan · Bank Road jewellers",
    "Oct – Feb, plus both Eids",
    "PKR 350 – 700 / tola over Karachi",
    "22K traditional sets",
    { label: "Supply note", value: "Restocked mainly via Peshawar" },
    [
      {
        q: "Mardan ka rate Peshawar se kitna upar hota hai?",
        a: "Aam taur par PKR 100–200 per tola, kyunki stock Peshawar se aage aata hai. Peshawar ka apna premium pehle se national base se upar hota hai, to Mardan ka total premium mulk ke oonche premiums mein aata hai.",
      },
      {
        q: "Traditional heavy set par making kitni parti hai?",
        a: "Haath ka kaam hone ki wajah se 10–20% tak. Machine-made light design par 6–10%. Making resale par wapas nahi milti — ye faisla design chunte waqt zehan mein rakhein.",
      },
      {
        q: "Rate din mein kitni baar badalta hai?",
        a: "Local board aam taur par din mein ek ya do baar set hota hai, lekin asal metal poore din hilta hai. Is page ka figure har 10 minute par dobara banta hai, is liye sauday se theek pehle isse dekh lena behtar hai.",
      },
    ],
  ),

  abbottabad: pk(
    "Sarafa Bazaar Abbottabad · Main Bazaar",
    "Oct – Feb, plus summer visitor season",
    "PKR 350 – 700 / tola over Karachi",
    "22K sets, 21K daily wear",
    { label: "Local note", value: "Summer tourism lifts retail demand" },
    [
      {
        q: "Abbottabad mein garmiyon mein rate barh jata hai?",
        a: "Metal ka base rate nahi — wo international spot par chalta hai. Lekin garmiyon mein sailani aur overseas visitors ki wajah se retail demand barhti hai aur dukandar making charge par kam narmi dikhate hain. Farq making mein hota hai, rate mein nahi.",
      },
      {
        q: "Yahan bullion bar lena theek rahega?",
        a: "Market chhoti hai, is liye bara weight order par aata hai. Sirf bachat ke liye lena hai to bar hi behtar hai (making na ke barabar), lekin ek do din ka intezaar karna par sakta hai.",
      },
      {
        q: "Hallmark ke bagair purana zewar bechna kaisa rehta hai?",
        a: "Bik jata hai lekin katauti barh jati hai — bina stamp par 8–12%, stamped par 5–8% aam hai. Purity test apne saamne karwayein aur reading likhwa lein.",
      },
    ],
  ),

  "toba-tek-singh": pk(
    "Sarafa Bazaar Toba Tek Singh",
    "Nov – Feb (shaadi season)",
    "PKR 300 – 650 / tola over Karachi",
    "22K sets",
    { label: "Supply note", value: "Restocked via Faisalabad" },
    [
      {
        q: "Toba Tek Singh mein gold rate kaise tay hota hai?",
        a: "Base wahi hai jo poore mulk ka — international spot aur USD/PKR. Local hissa sirf premium (PKR 300–650 per tola) aur making charge hai. Stock zyadatar Faisalabad se aata hai.",
      },
      {
        q: "Kya Faisalabad jaakar lena sasta parega?",
        a: "Rate mein farq chand sau rupay per tola ka hai. Chhote khareedar ke liye safar wajib nahi; 5 tola se upar ke set par Faisalabad ki making charge aur variety ka faida ban sakta hai.",
      },
      {
        q: "Yahan gold kis unit mein bikta hai?",
        a: "Tola hi rozmarra unit hai (1 tola = 11.6638 gram), chhote pieces gram mein. Is page par dono figures saath diye gaye hain.",
      },
    ],
  ),

  jhang: pk(
    "Sarafa Bazaar Jhang · Chenab Chowk area",
    "Nov – Feb (shaadi season)",
    "PKR 300 – 650 / tola over Karachi",
    "22K sets",
    { label: "Supply note", value: "Restocked via Faisalabad / Lahore" },
    [
      {
        q: "Jhang mein aaj ka rate kaise check karein?",
        a: "Is page ka per-tola aur per-gram figure market hours mein har 10 minute par dobara banta hai. Local counter quote us par premium aur making charge jorh kar banta hai.",
      },
      {
        q: "Chhote shehron mein premium zyada kyun hota hai?",
        a: "Kam volume aur zyada handling. Metal Karachi port se aata hai aur har intermediate step apna margin jorhta hai. Is ka spot price se koi taalluq nahi.",
      },
      {
        q: "Zewar bechne se pehle kya karna chahiye?",
        a: "Do se teen dukanon se quote lein, wazan saamne tulwayein aur deduction percentage pehle tay karein. Aam range 5–10% hai; bina hallmark par isse zyada.",
      },
    ],
  ),
};

/* ─── INDIA ────────────────────────────────────────────────────────── */

export const IN_GOLD_EXTRA: Record<string, ExtraCityData> = {
  chennai: inC(
    "NSC Bose Road · T. Nagar (Panagal Park) jewellers",
    "Akshaya Tritiya, Diwali, Nov – Feb weddings",
    "Often the highest metro rate in India",
    "22K — Tamil buyers prefer high-purity",
    { label: "Local quirk", value: "Quoted per gram, 22K-led market" },
    [
      {
        q: "Chennai gold rate India mein aksar sabse zyada kyun hota hai?",
        a: "Chennai ki demand overwhelmingly 22K aur usse upar ki purity ki hai, aur yahan ke bade jeweller apna hi refining/sourcing premium rakhte hain. Iske ilawa Tamil Nadu ke local dealer association ka quote historically national average se thoda upar rehta hai. Metal ka base wahi hai; farq local layer ka hai.",
      },
      {
        q: "T. Nagar mein gold kharidte waqt making charge kitni hoti hai?",
        a: "Plain chains aur bangles par 6–12%, antique aur temple-jewellery designs par 15–25% tak. Bade showroom aksar 'wastage' aur 'making' alag alag dikhate hain — dono ka total poochein, sirf ek number par faisla na karein.",
      },
      {
        q: "Chennai mein 916 hallmark ka matlab kya hai?",
        a: "916 ka matlab 91.6% purity yaani 22K. BIS hallmark ke saath ab HUID (6-digit alphanumeric) bhi hona chahiye. Bill par purity, net weight, making charge aur GST alag alag likhe hone chahiye — yehi resale ke waqt aap ka sabse bara sahara hai.",
      },
    ],
    [{ href: "/city/india/bangalore", label: "Gold rate Bangalore" }],
  ),

  kolkata: inC(
    "Bowbazar · Bhowanipore jewellery cluster",
    "Durga Puja, Dhanteras, Poila Boishakh",
    "Usually close to the national base",
    "22K — light filigree and daily wear",
    { label: "Speciality", value: "Bengali filigree, lightweight designs" },
    [
      {
        q: "Kolkata mein gold rate Chennai se kam kyun rehta hai?",
        a: "Kolkata ki demand light-weight designs ki taraf hai aur Bowbazar ka wholesale cluster bohot competitive hai, is liye dealer margin patla rehta hai. Chennai ka high-purity, high-volume retail model apna premium carry karta hai. Base spot rate dono jagah ek hi hai.",
      },
      {
        q: "Bowbazar se lena showroom se sasta parta hai?",
        a: "Metal ka rate lagbhag same hota hai — asal farq making charge mein hai. Bowbazar ke karigar-linked dealers 5–10% making par kaam kar lete hain jahan bade branded showroom 12–20% lete hain. Lekin hallmark aur HUID par samjhauta na karein, chahe kahin se bhi lein.",
      },
      {
        q: "Durga Puja se pehle rate barh jata hai?",
        a: "Metal ka rate international market par chalta hai, festival par nahi. Jo cheez barhti hai wo hai demand — is se making charge aur discount ki gunjaish kam ho jati hai. Behtar deal aksar festival ke baad ke sust hafton mein milti hai.",
      },
    ],
  ),

  bangalore: inC(
    "Commercial Street · Chickpet · Jayanagar",
    "Akshaya Tritiya, Ugadi, Nov – Feb weddings",
    "Close to Chennai, slightly below",
    "22K jewellery, growing 24K coin demand",
    { label: "Buyer profile", value: "Salaried IT buyers, SIP-style coin buying" },
    [
      {
        q: "Bangalore mein gold coin lena jewellery se behtar hai?",
        a: "Agar maqsad sirf bachat/investment hai to haan — coin aur bar par making charge 1–3% hoti hai, jabki jewellery par 8–20%, aur wo making resale par wapas nahi milti. Jewellery tab lein jab pehenna ho. Bangalore mein monthly coin buying ka rujhan isi wajah se barha hai.",
      },
      {
        q: "Chickpet aur Commercial Street mein kya farq hai?",
        a: "Chickpet wholesale-leaning hai — making charge kam, design range traditional. Commercial Street aur Jayanagar retail showrooms hain — design zyada, making zyada. Metal rate dono jagah ek hi base par chalta hai.",
      },
      {
        q: "Bangalore ka rate Chennai se kam hai?",
        a: "Aam taur par thora kam, lekin farq mamooli hai — kuch rupay per gram. Dono South India ke sourcing network par hain. Bara farq making charge aur GST ke baad ke final bill mein banta hai, base rate mein nahi.",
      },
    ],
    [{ href: "/city/india/chennai", label: "Gold rate Chennai" }],
  ),

  "hyderabad-in": inC(
    "Begum Bazaar · Charminar (Laad Bazaar) · Punjagutta",
    "Bonalu, Dussehra, Nov – Feb weddings",
    "Close to the national base",
    "22K sets; pearls and gold combination work",
    { label: "Speciality", value: "Pearl-and-gold Hyderabadi work" },
    [
      {
        q: "Charminar ke paas gold sasta milta hai?",
        a: "Laad Bazaar aur Begum Bazaar mein making charge retail showrooms se kam hoti hai, is liye final bill sasta ban sakta hai. Metal ka rate wahi rehta hai jo is page par hai. Hallmark aur HUID zaroor check karein — purani gali ki dukan par bhi ye laazim hai.",
      },
      {
        q: "Hyderabadi pearl-gold sets ka rate kaise nikalta hai?",
        a: "Aise pieces mein gold ka wazan aur moti ka wazan alag alag hota hai. Sirf gold wale hisse par is page ka rate lagta hai; moti ki qeemat alag se tay hoti hai aur uska koi standard live rate nahi hota. Bill par dono ka break-up alag maangein.",
      },
      {
        q: "Hyderabad mein 22K aur 18K mein se kya chunein?",
        a: "22K (91.6%) traditional sets aur resale value ke liye. 18K (75%) modern, stone-studded aur light designs ke liye — sakht hota hai, stone achhe se pakarta hai, aur per-piece qeemat kam aati hai. Resale mein 18K kam deta hai.",
      },
    ],
  ),

  ahmedabad: inC(
    "Manek Chowk · Ratanpole jewellery market",
    "Diwali/Dhanteras, Akshaya Tritiya, wedding season",
    "Close to the national base",
    "22K sets, strong 24K bar/coin demand",
    { label: "Buyer profile", value: "Trader community — heavy bullion buying" },
    [
      {
        q: "Manek Chowk gold market ka kya maqam hai?",
        a: "Manek Chowk India ke sabse purane aur bare jewellery bazaars mein se ek hai, aur Gujarat ki trading community ki wajah se yahan bullion (bar/coin) ki demand jewellery jitni hi mazboot hai. Competitive hone ki wajah se making charge national average se aksar neeche rehti hai.",
      },
      {
        q: "Ahmedabad mein bar aur coin par kitna premium lagta hai?",
        a: "Bank aur bade dealer ke bar par aam taur par 1–3% premium, jewellery-shop coin par thora zyada. Yaad rahe bank se khareeda bar bank wapas nahi khareedta — resale ke liye dealer hi chahiye hoga. Ye baat khareedne se pehle tay karein.",
      },
      {
        q: "Dhanteras par gold lena chahiye?",
        a: "Rawaayat ke lehaz se haan, lekin qeemat ke lehaz se ye India ka sabse bhira din hota hai — making par discount ki gunjaish kam hoti hai. Sirf bachat ke maqsad se lena hai to koi bhi aam din wahi metal deta hai, aksar behtar making par.",
      },
    ],
  ),

  pune: inC(
    "Laxmi Road · Raviwar Peth jewellers",
    "Gudi Padwa, Diwali, Nov – Feb weddings",
    "Tracks Mumbai closely",
    "22K sets, 18K modern designs",
    { label: "Local note", value: "Usually within a rupee or two of Mumbai" },
    [
      {
        q: "Pune ka gold rate Mumbai se alag hota hai?",
        a: "Bohot kam — dono Maharashtra ke ek hi supply network par hain, is liye per-gram farq aam taur par ek do rupay ka hi hota hai. Asal farq showroom ki making charge aur design range mein hai.",
      },
      {
        q: "Laxmi Road par making charge kitni hoti hai?",
        a: "Traditional Maharashtrian pieces (thushi, kolhapuri saaj) par 12–20% tak, kyunki kaam bareek hai. Plain chain aur bangle par 6–12%. Order dete waqt making ko percentage aur rupees dono mein likhwana behtar hai.",
      },
      {
        q: "Gudi Padwa par gold lena mehnga parta hai?",
        a: "Metal ka rate tyohar se nahi badalta — wo international spot par chalta hai. Lekin us din demand ki wajah se making par discount kam milta hai. Agar rawaayat zaroori nahi to kisi sust din behtar sauda ban jata hai.",
      },
    ],
    [{ href: "/city/india/mumbai", label: "Gold rate Mumbai" }],
  ),

  jaipur: inC(
    "Johari Bazaar · Gopalji Ka Rasta",
    "Teej, Diwali, Nov – Feb weddings",
    "Close to the national base",
    "22K sets; kundan and meenakari work",
    { label: "Speciality", value: "Kundan, polki and meenakari jewellery" },
    [
      {
        q: "Jaipur ke kundan set mein gold ka hissa kaise nikalta hai?",
        a: "Kundan aur polki pieces mein gold ka net weight aksar total weight se kaafi kam hota hai — beech mein lac (laakh) filling aur stones hote hain. Is page ka rate sirf gold ke net weight par lagta hai. Bill par 'net gold weight' alag se likhwana yahan sabse zaroori baat hai.",
      },
      {
        q: "Johari Bazaar se khareedna mehfooz hai?",
        a: "Bade aur purane dealers BIS hallmark aur HUID ke saath bill dete hain — yehi aap ki hifazat hai. Stone-studded pieces mein stone ki qeemat ka koi regulated rate nahi hota, is liye gold aur stone ka break-up alag maangein aur buy-back policy tehreer mein lein.",
      },
      {
        q: "Meenakari kaam par making charge kitni hai?",
        a: "Kaafi zyada — 15–30% tak — kyunki ye poora haath ka hunar hai. Ye charge resale par wapas nahi milti. Agar maqsad bachat hai to plain 22K ya bullion lein; meenakari tab lein jab piece khud maqsad ho.",
      },
    ],
  ),

  lucknow: inC(
    "Chowk · Aminabad jewellery market",
    "Dhanteras, Nov – Feb weddings",
    "Close to the national base",
    "22K sets, light chains",
    { label: "Speciality", value: "Chikankari-era traditional light sets" },
    [
      {
        q: "Lucknow mein gold rate kis waqt update hota hai?",
        a: "Is page ka figure market hours mein har 10 minute par khud dobara banta hai. Chowk aur Aminabad ke dealer apna board subah set karte hain aur bara move hone par dobara — is liye dopahar mein live figure zyada durust hota hai.",
      },
      {
        q: "Aminabad se lena showroom se sasta hai?",
        a: "Metal rate wahi rehta hai; farq making charge ka hai. Aminabad ke karigar-linked dealers 6–12% par kaam karte hain, branded showroom 12–20%. Hallmark aur HUID dono jagah laazim hai — is par samjhauta na karein.",
      },
      {
        q: "Purana zewar exchange karna behtar hai ya bechna?",
        a: "Exchange par jeweller aksar kam deduction lagate hain (3–5%) bashart ke aap wahin se naya lein; seedha cash bechne par 8–12% tak kat sakti hai. Dono soorton mein purity test apne saamne karwayein aur reading likhwayein.",
      },
    ],
  ),

  surat: inC(
    "Mahidharpura · Varachha jewellery belt",
    "Diwali/Dhanteras, wedding season",
    "Close to the national base",
    "22K sets; very strong diamond-jewellery overlap",
    { label: "Speciality", value: "Diamond-cutting hub — gold+diamond pieces" },
    [
      {
        q: "Surat mein diamond jewellery par gold rate kaise apply hota hai?",
        a: "Sirf gold ke net weight par. Diamond ki qeemat carat, cut, clarity aur colour par alag se tay hoti hai aur uska koi live public rate nahi hota. Bill par gold weight, gold rate, diamond value aur making — chaaron alag maangein, warna comparison mumkin nahi.",
      },
      {
        q: "Surat mein gold sasta milta hai?",
        a: "Metal ka rate national base par hi chalta hai. Faida making charge mein hai — manufacturing hub hone ki wajah se yahan making competitive rehti hai, khaas taur par diamond-set pieces par. Ye faida final bill mein dikhta hai, rate mein nahi.",
      },
      {
        q: "Diamond piece resale par kitna wapas milta hai?",
        a: "Gold ka hissa market rate par (minus deduction) wapas mil jata hai, lekin diamond par buy-back aksar khareed qeemat se kaafi kam hota hai aur jeweller ki policy par depend karta hai. Khareedne se pehle buy-back policy tehreer mein lena sabse zaroori qadam hai.",
      },
    ],
  ),

  kanpur: inC(
    "Naya Ganj · Birhana Road jewellers",
    "Dhanteras, Nov – Feb weddings",
    "Close to the national base",
    "22K sets",
    { label: "Buyer profile", value: "Trading and industrial-town demand" },
    [
      {
        q: "Kanpur mein aaj ka 22K rate kya hai?",
        a: "Is page ka 22K per-gram aur per-10-gram figure har 10 minute par international spot aur USD/INR se dobara banta hai. Counter par isme making charge aur GST add hoga.",
      },
      {
        q: "Kanpur ka rate Lucknow se alag hai?",
        a: "Lagbhag nahi — dono Uttar Pradesh ke ek hi supply network par hain aur farq per gram ek do rupay ka hota hai. Faisla making charge aur hallmark par karein, rate par nahi.",
      },
      {
        q: "GST kitna lagta hai aur kya wo rate mein shamil hai?",
        a: "Is page par diya gaya rate metal ka rate hai — GST usme shamil nahi. India mein gold jewellery par GST khareedari ke waqt metal value + making par lagta hai. Bill par GST alag line mein dikhna chahiye.",
      },
    ],
  ),

  nagpur: inC(
    "Itwari · Sitabuldi jewellery market",
    "Diwali, Nov – Feb weddings",
    "Close to the national base",
    "22K sets, 24K coins",
    { label: "Local note", value: "Central-India distribution point" },
    [
      {
        q: "Nagpur ka rate Mumbai se kitna alag hai?",
        a: "Bohot kam — per gram ek do rupay ka farq. Nagpur central India ka distribution point hai aur supply chain chhoti hai. Asal farq making charge aur showroom ki policy mein hai.",
      },
      {
        q: "Nagpur mein coin lena kahan se behtar hai?",
        a: "Bade jeweller aur bank dono coin bechte hain. Bank ka coin aksar tamper-proof packing mein hota hai lekin bank use wapas nahi khareedta — resale ke liye jeweller chahiye hoga. Is liye bohot log jeweller se hi lete hain jo buy-back tehreer mein deta ho.",
      },
      {
        q: "Rate kis waqt sabse zyada hilta hai?",
        a: "Jab international market khulta hai aur jab bara US data aata hai. Local bazaar ke khulne band hone se metal ka rate nahi hilta — sirf counter par milne wala quote ruk jata hai.",
      },
    ],
  ),

  indore: inC(
    "Sarafa Bazaar Indore (Rajwada area)",
    "Diwali/Dhanteras, wedding season",
    "Close to the national base",
    "22K sets",
    { label: "Local quirk", value: "Sarafa Bazaar turns into a night food street" },
    [
      {
        q: "Indore Sarafa Bazaar din mein jewellery aur raat mein khana — sach hai?",
        a: "Haan, ye Indore ki mashhoor cheez hai: din mein wahi galiyan sarafa (bullion) market hoti hain aur raat ko famous street-food bazaar ban jati hain. Jewellery khareedne ke liye din ka waqt hi theek hai; raat ko zyadatar dukanein band hoti hain.",
      },
      {
        q: "Indore mein making charge kitni hoti hai?",
        a: "Plain 22K sets par 6–12% aam hai; designer aur stone work par 15% se upar. Sarafa ke wholesale-leaning dealers bade showrooms se sasta parte hain, lekin hallmark aur HUID par samjhauta na karein.",
      },
      {
        q: "Kya Indore ka rate Bhopal se alag hai?",
        a: "Farq na ke barabar hai — dono Madhya Pradesh ke ek hi supply route par hain. Faisla making charge, design aur buy-back policy par karein.",
      },
    ],
    [{ href: "/city/india/bhopal", label: "Gold rate Bhopal" }],
  ),

  bhopal: inC(
    "Chowk Bazaar · New Market jewellers",
    "Diwali, Nov – Feb weddings",
    "Close to the national base",
    "22K sets",
    { label: "Supply note", value: "Largely restocked via Indore / Mumbai" },
    [
      {
        q: "Bhopal mein gold rate kaise tay hota hai?",
        a: "Base wahi hai jo poore India ka — international spot aur USD/INR — aur us par local dealer margin lagta hai. Bhopal ka stock zyadatar Indore aur Mumbai se aata hai, is liye farq mamooli rehta hai.",
      },
      {
        q: "Chowk Bazaar ya New Market — kahan se lein?",
        a: "Chowk Bazaar traditional aur wholesale-leaning hai (making kam), New Market retail showroom belt hai (design zyada, making zyada). Metal ka rate dono jagah lagbhag ek hi hai.",
      },
      {
        q: "Hallmark ke bina zewar khareedna theek hai?",
        a: "Nahi. India mein BIS hallmark aur HUID ke bagair hallmarked jewellery bechna qanooni tor par mehdood hai, aur bina stamp wale piece ki resale value kaafi gir jati hai. Bill par purity, net weight aur HUID zaroor check karein.",
      },
    ],
  ),

  patna: inC(
    "Bakerganj · Thakurbari Road jewellers",
    "Chhath, Diwali, Nov – Feb weddings",
    "Close to the national base",
    "22K sets",
    { label: "Local note", value: "Chhath and wedding season drive demand" },
    [
      {
        q: "Patna mein gold ki demand kab peak par hoti hai?",
        a: "Chhath ke aas paas aur Nov–Feb ke shaadi season mein. Metal ka rate isse nahi badalta (wo international market par chalta hai), lekin us dauran making charge par discount ki gunjaish kam ho jati hai.",
      },
      {
        q: "Patna mein 22K set par kitni making lagti hai?",
        a: "Aam taur par 8–15%, design ki bareeki par depend karta hai. Order dene se pehle making ko rupees mein likhwayein — sirf percentage par bharosa karne se final bill par tanaza hota hai.",
      },
      {
        q: "Purana zewar bechne par kitna milta hai?",
        a: "Hallmark wale piece par market rate se 5–8% deduction aam hai; bina hallmark ke purity test hota hai aur katauti 8–12% tak ja sakti hai. Do dukanon se quote lena hamesha behtar hai.",
      },
    ],
  ),

  vadodara: inC(
    "Mandvi · Raopura jewellery market",
    "Navratri, Diwali, wedding season",
    "Tracks Ahmedabad closely",
    "22K sets, 24K coins",
    { label: "Supply note", value: "Within the Ahmedabad–Surat gold belt" },
    [
      {
        q: "Vadodara ka rate Ahmedabad se alag hai?",
        a: "Bohot kam farq — dono Gujarat ke ek hi bullion belt par hain. Per gram ek do rupay ka antar hota hai. Faisla making charge aur buy-back policy par karein.",
      },
      {
        q: "Navratri par gold lena chahiye?",
        a: "Metal ka rate tyohar se nahi hilta. Us dauran demand barhti hai jis se making par discount kam milta hai. Sirf bachat ke liye lena ho to sust dinon mein wahi metal aksar behtar making par mil jata hai.",
      },
      {
        q: "Coin aur jewellery mein se kya lein?",
        a: "Bachat ke liye coin/bar (making 1–3%), pehnne ke liye jewellery (making 8–20%). Jewellery ki making resale par wapas nahi milti — ye ek hi jumla hai jo sabse zyada paisa bachata hai.",
      },
    ],
  ),

  ludhiana: inC(
    "Chaura Bazaar · Ghumar Mandi jewellers",
    "Lohri, Baisakhi, Nov – Feb weddings",
    "Close to the national base",
    "22K heavy sets",
    { label: "Buyer profile", value: "Industrial + NRI-remittance demand" },
    [
      {
        q: "Ludhiana mein heavy Punjabi set ka hisab kaise lagayein?",
        a: "Set ka gram wazan lein, is page ke 22K per-gram rate se metal value nikalein, phir making 10–18% aur uske baad GST jorhein. Heavy sets par making percentage par mol-tol mumkin hota hai — bulk order par zaroor poochein.",
      },
      {
        q: "NRI ke liye Ludhiana se gold lena faidemand hai?",
        a: "Metal ka rate poore India mein lagbhag ek hi hai, to faida sirf making aur design mein hai. Mulk se bahar le jane ke customs qawaneen alag mamla hai — safar se pehle current allowance khud tasdeeq karein; ham qanooni mashwara nahi dete.",
      },
      {
        q: "Buy-back policy kya poochni chahiye?",
        a: "Teen cheezein tehreer mein: deduction percentage, kya wo sirf apne bechay hue piece par hai ya kisi bhi hallmarked piece par, aur purity test kaun karega. Ye teen jawab hi baad mein hazaron ka farq banate hain.",
      },
    ],
  ),

  coimbatore: inC(
    "Raja Street · Oppanakara Street jewellers",
    "Akshaya Tritiya, Diwali, Tamil wedding season",
    "Tracks Chennai closely",
    "22K — high-purity preference",
    { label: "Local note", value: "Follows the Tamil Nadu 22K-led pattern" },
    [
      {
        q: "Coimbatore ka rate Chennai se kam hai?",
        a: "Aam taur par bilkul qareeb — dono Tamil Nadu ke ek hi association pattern ko follow karte hain aur 22K-led demand rakhte hain. Farq per gram chand rupay ka hota hai.",
      },
      {
        q: "Raja Street par making charge kitni hoti hai?",
        a: "Plain 22K chains aur bangles par 6–12%; temple aur antique work par 15–25%. Kai showroom 'wastage' alag dikhate hain — making aur wastage dono ka total poochein.",
      },
      {
        q: "Monthly gold saving scheme lena chahiye?",
        a: "Ye jeweller ka apna scheme hota hai, bank deposit nahi — is liye terms dhyan se parhein: kya maturity par metal rate us din ka lagega, kya making charge waive hoti hai, aur cancel karne par kya milta hai. Regulated investment ke liye coin/bar ya sovereign instruments zyada shaffaf hain.",
      },
    ],
    [{ href: "/city/india/chennai", label: "Gold rate Chennai" }],
  ),

  visakhapatnam: inC(
    "Jagadamba Junction · Kurupam Market jewellers",
    "Diwali, Ugadi, Nov – Feb weddings",
    "Close to the national base",
    "22K sets",
    { label: "Local note", value: "Coastal Andhra's main jewellery market" },
    [
      {
        q: "Visakhapatnam mein aaj ka gold rate kya hai?",
        a: "Is page ka per-gram aur per-10-gram figure har 10 minute par international spot aur USD/INR se dobara banta hai. Counter quote us par making charge aur GST jorh kar banega.",
      },
      {
        q: "Vizag ka rate Hyderabad se alag hai?",
        a: "Farq mamooli hai — per gram chand rupay. Dono Telugu-belt ke supply network par hain. Faisla making charge, hallmark aur buy-back policy par karein.",
      },
      {
        q: "Gold khareedte waqt bill par kya kya hona chahiye?",
        a: "Net gold weight, purity (916/750), us din ka per-gram rate, making charge, stone value (agar ho) aur GST — sab alag alag lines mein. HUID number bhi hona chahiye. Yehi bill resale ke waqt aap ki sabse mazboot daleel hai.",
      },
    ],
  ),
};

export function extraGoldCityData(
  country: "pakistan" | "india",
  slug: string,
): ExtraCityData | null {
  const src = country === "pakistan" ? PK_GOLD_EXTRA : IN_GOLD_EXTRA;
  return src[slug] ?? null;
}
